<template>
  <div class="container mt-5 px-2">
    <article class="message is-danger" v-if="!token">
      <div class="message-body">
        You have not signed in yet. Please go back to <a href="/">home page</a> to sign in first.
      </div>
    </article>
    <div v-if="token" class="pb-6">
      <nav class="breadcrumb" aria-label="breadcrumbs">
        <ul>
          <li><router-link :to="vendorId ? ('/vendor-inbounds/' + vendorId) : '/inbounds'">Inbound shipments</router-link></li>
          <li class="is-active"><a>{{ inboundId }}</a></li>
        </ul>
      </nav>
      <div class="mt-3">
        <article class="message is-warning" v-if="dutyWarning">
          <div class="message-header">
            <p>Duty warning</p>
          </div>
          <div class="message-body">
            {{ dutyWarning }}
          </div>
        </article>
        <div v-if="waiting || !inbound">
          <span class="icon is-medium is-size-4">
            <i class="fas fa-spinner fa-pulse"></i>
          </span>
        </div>
        <div v-else>
          <span class="is-pulled-right">
            <a v-if="inbound && inbound.draftStatus == 'Submitted'" class="button mr-2" :class="{'is-loading': sending}" @click="putBackDraft">Put back draft</a>
            <a v-if="inbound && inbound.draftStatus == 'Submitted'" class="button mr-2" :class="{'is-loading': syncing}" @click="syncFromNS">Sync from NS</a>
          </span>
          <h1 class="title is-5 mt-6">Primary info</h1>
          <div class="columns is-multiline mt-3">

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Shipment#</label>
                <div class="control">
                  <input class="input is-static"  type="text" v-model="inbound.shipmentNumber" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">External doc id</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.bookingId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">NS Id</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.nsId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">PO Id</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.poId" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Vendor</label>
                <div class="control">
                  <textarea class="textarea" placeholder="Textarea" readonly>{{ inbound.vendorName }}</textarea>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Draft status</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.draftStatus" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Status</label>
                <div class="control">
                  <div class="select">
                    <select v-model="inbound.shipmentStatus" :disabled="!editable">
                      <option>toBeShipped</option>
                      <option>inTransit</option>
                      <option disabled>partiallyReceived</option>
                      <option disabled>received</option>
                      <option disabled>closed</option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Status</label>
                <div class="field has-addons">
                  <p class="control">
                    <span class="select">
                      <select v-model="newAttributes.shipmentStatus">
                        <option>toBeShipped</option>
                        <option>inTransit</option>
                        <option>partiallyReceived</option>
                        <option>received</option>
                        <option>closed</option>
                      </select>
                    </span>
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.shipmentStatus" :class="{'is-loading': sending}" @click="updateAttribute('shipmentStatus')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Note</label>
                <div class="control">
                  <textarea class="textarea" :readonly="!editable" v-model="inbound.memo"></textarea>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">MBL</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.mbl" :readonly="!editable">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">MBL</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" v-model="newAttributes.mbl">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.mbl" :class="{'is-loading': sending}" @click="updateAttribute('mbl')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">HBL</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.hbl" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Created Date</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.shipmentCreatedDate" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Location</label>
                <div class="control">
                  <input class="input is-static" type="text" v-model="inbound.locationName" readonly>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Carrier</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.carrier" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Vessel</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.vessel" :readonly="!editable">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Vessel</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" v-model="newAttributes.vessel">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.vessel" :class="{'is-loading': sending}" @click="updateAttribute('vessel')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Voyage</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.voyage" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Freight Forwarder</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.freightForwarder" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Place of receipt</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.placeOfReceipt" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Place of delivery</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.placeOfDelivery" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Load Port</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.loadPort" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3">
                <label class="label">Discharge Port</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.dischargePort" :readonly="!editable">
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Expected shipping Date</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}"  type="text" v-model="inbound.expectedShippingDate" :readonly="!editable" placeholder="YYYY-MM-DD">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Expected shipping Date</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newAttributes.expectedShippingDate">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.expectedShippingDate" :class="{'is-loading': sending}" @click="updateAttribute('expectedShippingDate')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Actual shipping date</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}"  type="text" v-model="inbound.actualShippingDate" :readonly="!editable" placeholder="YYYY-MM-DD">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Actual shipping date</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newAttributes.actualShippingDate">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.actualShippingDate" :class="{'is-loading': sending}" @click="updateAttribute('actualShippingDate')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Expected delivery Date</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.expectedDeliveryDate" :readonly="!editable" placeholder="YYYY-MM-DD">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Expected delivery Date</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newAttributes.expectedDeliveryDate">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.expectedDeliveryDate" :class="{'is-loading': sending}" @click="updateAttribute('expectedDeliveryDate')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-3">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Actual delivery date</label>
                <div class="control">
                  <input class="input" :class="{'is-static': !editable}" type="text" v-model="inbound.actualDeliveryDate" :readonly="!editable" placeholder="YYYY-MM-DD">
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Actual delivery date</label>
                <div class="field has-addons">
                  <p class="control">
                    <input class="input" type="text" placeholder="YYYY-MM-DD" v-model="newAttributes.actualDeliveryDate">
                  </p>
                  <p class="control">
                    <button class="button" :disabled="!newAttributesChanged.actualDeliveryDate" :class="{'is-loading': sending}" @click="updateAttribute('actualDeliveryDate')">
                      <i class="fas fa-paper-plane"></i>
                    </button>
                  </p>
                </div>
              </div>
            </div>

            <div class="column is-12">
              <div class="field mt-3" v-if="vesselData">
                <label class="label">
                  <span>Vessel location</span>
                  <router-link class="ml-2" :to="'/inbounds-map/'+inbound.id">See all</router-link>
                </label>
                <div class="control" v-if="vesselData.error">
                  <article class="message is-info">
                    <div class="message-body">
                      {{ vesselData.error }}
                    </div>
                  </article>
                </div>
                <div class="control" v-else>
                  <GmapMap
                    :center="mapCenter"
                    :zoom="5"
                    map-type-id="terrain"
                    style="width: 100%; height: 600px"
                  >
                    <GmapMarker
                      :position="mapMarker.position"
                      :icon="mapMarker.icon"
                      :label="mapMarker.label"
                      @click="infoWinOpen=true"
                    />
                    <GmapInfoWindow :options="infoOptions" :position="mapCenter" :opened="infoWinOpen" @closeclick="infoWinOpen=false">
                    </GmapInfoWindow>
                  </GmapMap>
                </div>

              </div>
              
            </div>

            <div class="column is-6">
              <div class="field mt-3" v-if="!canUpdate">
                <label class="label">Container invoice# mappings</label>
                <div class="field has-addons" v-for="(invoice, i) in inbound.invoiceNums" :key="'i-i-'+i">
                  <p class="control has-icons-left">
                    <input class="input" :class="{'is-static': !editable}" type="text" placeholder="Container" v-model="invoice.container">
                    <span class="icon is-small is-left">
                      <i class="fas fa-box"></i>
                    </span>
                  </p>
                  <p class="control has-icons-left">
                    <input class="input" :class="{'is-static': !editable}" type="text" placeholder="Invoice#"  v-model="invoice.invoiceNum">
                    <span class="icon is-small is-left">
                      <i class="fas fa-file-invoice-dollar"></i>
                    </span>
                  </p>
                  <p class="control" v-if="editable">
                    <a class="button" @click="removeInvoiceNum(i)">
                      <span class="icon is-small">
                        <i class="fas fa-minus"></i>
                      </span>
                    </a>
                  </p>
                </div>
                <div class="field has-addons" v-if="editable">
                  <p class="control">
                    <a class="button is-link" @click="addInvoiceNum">
                      <span class="icon is-small">
                        <i class="fas fa-plus"></i>
                      </span>
                    </a>
                  </p>
                </div>
              </div>
              <div class="field mt-3" v-if="canUpdate">
                <label class="label">Container invoice# mappings</label>
                <div class="field has-addons" v-for="(invoice, i) in newAttributes.invoiceNums" :key="'na-i-'+i">
                  <p class="control has-icons-left">
                    <input class="input" type="text" placeholder="Container" v-model="invoice.container">
                    <span class="icon is-small is-left">
                      <i class="fas fa-box"></i>
                    </span>
                  </p>
                  <p class="control has-icons-left">
                    <input class="input" type="text" placeholder="Invoice#"  v-model="invoice.invoiceNum">
                    <span class="icon is-small is-left">
                      <i class="fas fa-file-invoice"></i>
                    </span>
                  </p>
                </div>
                <div class="field has-addons">
                  <p class="control">
                    <a class="button" :disabled="!newAttributesChanged.invoiceNums" :class="{'is-loading': sending}"  @click="updateAttribute('invoiceNums')">
                      <span class="icon is-small">
                        <i class="fas fa-paper-plane"></i>
                      </span>
                    </a>
                  </p>
                </div>
              </div>
            </div>
            
          </div>

          <div class="field">
            <label class="label">Documents</label>
            <div class="control">
              <attachments :attachments="attachments" :canEdit="editable" @new-attachment-created="addAttachment" @attachment-deleted="removeAttachment" :preview="false" />
            </div>
          </div>

          <div class="mt-6" v-if="isVendorAdmin && inbound.draftStatus == 'Submitted'">
            <h1 class="title is-5">Landed cost</h1>
            <div v-if="newLandedCost.error" class="notification is-danger is-light mt-3">
              <button class="delete" @click="newLandedCost.error=''"></button>
              {{newLandedCost.error}}
            </div>
            <div class="notification is-success is-light p-3 mb-3" v-if="newLandedCost.freightBillNsId">
              The <strong>freight</strong> landed cost and venfor bill have been created successfully. The bill NS id is <strong>{{ newLandedCost.freightBillNsId }}</strong> with reference # of <strong>{{ newLandedCost.freightInvoiceNum }}</strong>
            </div>

            <div class="notification is-success is-light p-3 mb-3" v-if="newLandedCost.dutyBillNsId">
              The <strong>custom duty</strong> landed cost and venfor bill have been created successfully. The bill NS id is <strong>{{ newLandedCost.dutyBillNsId }}</strong> with reference # of <strong>{{ newLandedCost.dutyEntryNum }}</strong>
            </div>

            <div class="field">
              <div class="field has-addons">
                <div class="control">
                  <a class="button is-static">
                    Expense account
                  </a>
                </div>
                <div class="control is-expanded model-select-full-width">
                  <model-select :options="accountOptions" v-model="newLandedCost.expenseAccountNsId" placeholder="Expense account" />
                </div>
                <div class="control">
                  <span class="select">
                    <select v-model="newLandedCost.subsidiaryName" :disabled="Boolean(newLandedCost.freightBillNsId) || Boolean(newLandedCost.dutyBillNsId)">
                      <option :value="''" disabled>Subsidiary Name</option>
                      <option v-for="(so, i) in subsidiaryList" :key="'subsidiary-option-' + i">{{so}}</option>
                    </select>
                  </span>
                </div>
              </div>
            </div>

            <div class="field">
              <div class="field has-addons">
                <div class="control">
                  <a class="button is-static">
                    Freight vendor
                  </a>
                </div>
                <div class="control is-expanded model-select-full-width">
                  <model-select :options="vendorOptions" v-model="newLandedCost.freightVendorName" placeholder="Freight vendor" />
                </div>
                <div class="control model-select-350px">
                  <model-select :options="accountOptions" v-model="newLandedCost.freightAccountNsId" placeholder="Freight vendor account"/>
                </div>
              </div>
            </div>

            <div class="field">
              <div class="field has-addons">
                <div class="control">
                  <a class="button is-static">
                    Freight cost
                  </a>
                </div>
                <div class="control is-expanded">
                  <input class="input" type="text" placeholder="Invoice number" v-model="newLandedCost.freightInvoiceNum" >
                </div>
                <div class="control">
                  <input class="input amount-input" type="number" placeholder="Amount" v-model.number="newLandedCost.freightCost" >
                </div>
                <div class="control">
                  <input class="input date-input" type="text" placeholder="YYYY-MM-DD" v-model="newLandedCost.freightInvoiceDate" >
                </div>
                <div class="control" v-if="!newLandedCost.freightBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button" :class="{'is-loading': newLandedCost.saving}" @click="saveLandedCost">
                    Save draft
                  </button>
                </div>
                <div class="control" v-if="!newLandedCost.freightBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button is-link" :disabled="!newFreightCostValid" :class="{'is-loading': newLandedCost.saving}" @click="submitFreightCost">
                    Submit freight & Create bill
                  </button>
                </div>
                <div class="control" v-if="newLandedCost.freightBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button is-link" :disabled="!newFreightCostValid" :class="{'is-loading': newLandedCost.saving}" @click="submitFreightCost">
                    Save freight & Update bill
                  </button>
                </div>
              </div>
            </div>

            <div class="field">
              <div class="field has-addons">
                <div class="control">
                  <a class="button is-static">
                    Duty vendor
                  </a>
                </div>
                <div class="control is-expanded model-select-full-width">
                  <model-select :options="vendorOptions" v-model="newLandedCost.dutyVendorName" placeholder="Duty vendor" />
                </div>
                <div class="control model-select-350px">
                  <model-select :options="accountOptions" v-model="newLandedCost.dutyAccountNsId" placeholder="Duty vendor account"/>
                </div>
              </div>
            </div>

            <div class="field">
              <div class="field has-addons">
                <div class="control">
                  <a class="button is-static">
                    Duty cost
                  </a>
                </div>
                <div class="control is-expanded">
                  <input class="input" type="text" placeholder="Duty entry number" v-model="newLandedCost.dutyEntryNum" >
                </div>
                <div class="control">
                  <input class="input amount-input" type="number" placeholder="Amount" v-model.number="newLandedCost.dutyCost" >
                </div>
                <div class="control">
                  <input class="input date-input" type="text" placeholder="YYYY-MM-DD" v-model="newLandedCost.dutyInvoiceDate" >
                </div>
                <div class="control" v-if="!newLandedCost.dutyBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button" :class="{'is-loading': newLandedCost.saving}" @click="saveLandedCost">
                    Save draft
                  </button>
                </div>
                <div class="control" v-if="!newLandedCost.dutyBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button is-link" :disabled="!newDutyCostValid" :class="{'is-loading': newLandedCost.saving}" @click="submitDutyCost">
                    Submit duty & Create bill
                  </button>
                </div>
                <div class="control" v-if="newLandedCost.dutyBillNsId && inbound.shipmentStatus != 'closed'">
                  <button class="button is-link" :disabled="!newDutyCostValid" :class="{'is-loading': newLandedCost.saving}" @click="submitDutyCost">
                    Save duty & Update bill
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 table-container">
            <div class="columns pt-6">
              <div class="column">
                <h1 class="title is-5">Inbound shipment items</h1>
              </div>
              <div class="column">
                <div class="field">
                  <p class="control has-icons-left">
                    <input class="input" type="text" placeholder="Search" v-model="search">
                    <span class="icon is-small is-left">
                      <i class="fas fa-search"></i>
                    </span>
                  </p>
                </div>
              </div>
            </div>
            
            <table v-if="inbound.items" class="table is-full-width is-hoverable my-table">
              <thead>
                <th>#</th>
                <th>
                  <span class="is-clickable" @click="changeSortOption('purchaseOrderName')">PO</span>
                  <span class="icon" v-if="sortOption.field == 'purchaseOrderName'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th>
                  <span class="is-clickable" @click="changeSortOption('itemId')">Item</span>
                  <span class="icon" v-if="sortOption.field == 'itemId'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th>
                  <span class="is-clickable" @click="changeSortOption('shipmentItemName')">Name</span>
                  <span class="icon" v-if="sortOption.field == 'shipmentItemName'">
                    <i class="fas" :class="{'fa-sort-up': sortOption.asc, 'fa-sort-down': !sortOption.asc}"></i>
                  </span>
                </th>
                <th>Description</th>
                <th class="num-cell">
                  <span>Quantity expected</span>
                </th>
                <th>Containers</th>
                <th class="num-cell">Unit price</th>
                <th class="num-cell">Total price</th>
                <th class="num-cell">Tarriff %</th>
                <th class="num-cell">Total Tarriff</th>
                <th v-if="editable || canUpdate" class="action-column">
                  <span>Actions</span>
                </th>
              </thead>
              <tbody>
                <tr v-for="(ibi, i) in itemsDetails.items" :key="'ibi-' + inbound.id + '-i-' + i">
                  <td>{{ i + 1 }}</td>
                  <td>
                    <span v-if="!ibi.po">{{ ibi.purchaseOrderName }}</span>
                    <router-link v-else :to="vendorId ? ('/vendor-purchase-order/' + vendorId + '/' + ibi.po.id) : ('/purchase-order/' + ibi.po.id)">{{ibi.purchaseOrderName }}</router-link>
                  </td>
                  <td>
                    <span>{{ ibi.itemId }}</span>
                  </td>
                  <td>
                    <span>{{ ibi.shipmentItemName }}</span>
                  </td>
                  <td>{{ ibi.shipmentItemDescription }}</td>
                  <td class="num-cell">
                    <span>{{ ibi.quantityExpected }}</span>
                  </td>
                  <td>{{ ibi.containerInfo.replaceAll(',', ', ') }}</td>
                  <td class="num-cell">{{ ibi.poItem && formatPrice(ibi.poItem.rate) }}</td>
                  <td class="num-cell">{{ ibi.poItem && formatPrice(ibi.poItem.rate * ibi.quantityExpected) }}</td>
                  <td class="num-cell">{{ (ibi.poItem && ibi.tarriff != null) ? ('' + ibi.tarriff + '%') : 'N/A' }}</td>
                  <td class="num-cell">{{ (ibi.poItem && ibi.tarriff != null) ? formatPrice(ibi.poItem.rate * ibi.quantityExpected * ibi.tarriff / 100) : 'N/A' }}</td>
                  <td v-if="editable || canUpdate">
                      <a @click="openInboundItemModal(ibi)">
                        <span class="icon">
                          <i class="fas fa-edit"></i>
                        </span>
                      </a>
                      <a class="has-text-danger ml-3" @click="deleteInboundItem(ibi)">
                        <span class="icon">
                          <i class="fas fa-trash"></i>
                        </span>
                      </a>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th>Total</th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th class="num-cell">{{ (itemsDetails.totals && itemsDetails.totals.quantity) || '' }}</th>
                  <th></th>
                  <th></th>
                  <th class="num-cell">{{ (itemsDetails.totals && itemsDetails.totals.amount) ? formatPrice(itemsDetails.totals.amount) : '' }}</th>
                  <th></th>
                  <th class="num-cell">{{ (itemsDetails.totals && itemsDetails.totals.tarriff) ? formatPrice(itemsDetails.totals.tarriff) : '' }}</th>
                  <th v-if="editable || canUpdate"></th>
                </tr>
                <tr v-if="isVendorAdmin && inbound && newLandedCost.dutyCost">
                  <th>Actual duty</th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th></th>
                  <th class="num-cell"></th>
                  <th></th>
                  <th></th>
                  <th class="num-cell"></th>
                  <th></th>
                  <th class="num-cell">{{ formatPrice(newLandedCost.dutyCost) }}</th>
                  <th v-if="editable || canUpdate"></th>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="mb-3">
            <a v-if="editable || canUpdate" @click="openAddInboundItemsModal" class="button">Add items</a>
          </div>

          <div class="mb-6" v-if="editable">
            <div class="field has-addons">
              <div class="control">
                <div class="file has-name">
                  <label class="file-label">
                    <input class="file-input" type="file" accept=".csv" @change="onFileChange">
                    <span class="file-cta">
                      <span class="file-icon">
                        <i class="fas fa-upload"></i>
                      </span>
                      <span class="file-label">
                        Add items by upload
                      </span>
                    </span>
                    <span class="file-name">
                      {{file ? file.name : 'Choose a CSV file...'}}
                    </span>
                  </label>
                </div>
              </div>
              <div class="control">
                <button class="button" :disabled="!file" @click="upload">
                  Preview
                </button>
              </div>
            </div>
            <p class="help is-info upload-info">Retrieve "PO", "Line", "Quantity", "Container" columns from the CSV file to add items.</p>
          </div>

          <div v-if="inboundChanged" class="notification is-warning is-light">
            <div>You have unsaved changes!</div>
            <div v-if="canUpdate" class="mt-3">
              <button class="button is-danger" :class="{'is-loading': submitting}" @click="submitItems">Submit items</button>
            </div>
          </div>

          <div class="field is-grouped" v-if="editable">
            <div class="control">
              <button class="button is-link" :class="{'is-loading': saving}" :disabled="!inboundChanged || !canSave" @click="saveDraft">Save draft</button>
            </div>
            <div class="control" v-if="!inbound.nsId && inboundId != 'new'">
              <button class="button is-light is-danger" @click="deleteDraft">Delete draft</button>
            </div>
          </div>

          <div class="field is-grouped mt-5" v-if="vendorId && inbound.draftStatus == 'Draft'">
            <div class="control">
              <button class="button is-danger" :class="{'is-loading': submitting}" :disabled="!canSubmit" @click="submitFinal">Submit final</button>
            </div>
          </div>

          <div class="field is-grouped mt-5" v-if="isVendorAdmin && inbound.draftStatus == 'Submitted' && (inbound.poBills && !inbound.poBills.length)">
            <div class="control">
              <button class="button is-link" :disabled="!canCreatePoBills" :inbound="inbound" @click="createPoBillsModal.opened = true">Create PO Bills</button>
            </div>
          </div>

          <div class="field is-grouped mt-5" v-if="isVendorAdmin && inbound.draftStatus == 'Submitted' && (inbound.poBills && !inbound.poBills.length)">
            <div class="control">
              <button class="button is-link" :disabled="!canCreatePoBills" :inbound="inbound" @click="createPoBillsModal.opened = true">Create PO Bills</button>
            </div>
          </div>

          <div v-if="inbound.draftStatus == 'Submitted' && inbound.poBills && inbound.poBills.length">
            <article class="message is-success" v-if="succeededPoBills.length">
              <div class="message-header">
                <p>PO bills created</p>
              </div>
              <div class="message-body">
                <p v-for="(b, i) in succeededPoBills" :key="'vbs-'+i">
                  {{ b }}
                </p>
              </div>
            </article>
            <article class="message is-danger" v-if="failedPoBills.length">
              <div class="message-header">
                <p>PO bills failed to create</p>
              </div>
              <div class="message-body">
                <p v-for="(b, i) in failedPoBills" :key="'vbf-'+i">
                  {{ b }}
                </p>
              </div>
            </article>
          </div>

          <div class="field is-grouped mt-5" v-if="isVendorAdmin && inbound.draftStatus == 'Submitted' && (failedPoBills && failedPoBills.length)">
            <div class="control">
              <button class="button is-link" :disabled="!canCreatePoBills" :inbound="inbound" @click="createPoBillsModal.opened = true">Recreate PO Bills</button>
            </div>
          </div>

        </div>
      </div>
      <div v-if="error" class="notification is-danger is-light mt-3">
        <button class="delete" @click="error=''"></button>
        {{error}}
      </div>

      <inbound-item-modal :opened="inboundItemModal.opened" :pos="pos" :inbound="inbound" :item="inboundItemModal.item" @closeInboundItemModal="closeInboundItemModal"></inbound-item-modal>
      <add-inbound-items-modal :opened="addInboundItemsModal.opened" :pos="pos" :inbound="inbound" @closeAddInboundItemsModal="closeAddInboundItemsModal"></add-inbound-items-modal>
      <upload-inbound-items-modal :opened="uploadInboundItemsModal.opened" :pos="pos" :inbound="inbound" :records="uploadInboundItemsModal.records" @closeUploadInboundItemsModal="closeUploadInboundItemsModal"></upload-inbound-items-modal>
      <create-po-bills-modal :opened="createPoBillsModal.opened" :inbound="inbound" @closeCreatePoBillsModal="closeCreatePoBillsModal" @PoBillsCreated="PoBillsCreated"></create-po-bills-modal>
    </div>
  </div>
</template>

<script>
import InboundItemModal from '@/components/modals/InboundItemModal'
import AddInboundItemsModal from '@/components/modals/AddInboundItemsModal'
import UploadInboundItemsModal from '@/components/modals/UploadInboundItemsModal'
import CreatePoBillsModal from '@/components/modals/CreatePoBillsModal'
import Attachments from '@/components/attachments/Attachments'
import InboundModal from '@/components/modals/InboundModal'
import { parse } from 'csv-parse/lib/sync'
import { ModelSelect } from 'vue-search-select'
import { Converter } from 'showdown'
import dateFormat from 'dateformat'

const converter = new Converter()
const dateFormatter = (date) => {
  const d = new Date(date * 1000)
  return dateFormat(d, 'yyyy-mm-dd HH:MM')
}

export default {
  name: 'Inbound',
  components: {
    InboundItemModal,
    AddInboundItemsModal,
    UploadInboundItemsModal,
    CreatePoBillsModal,
    Attachments,
    ModelSelect
  },
  data () {
    return {
      error: '',
      waiting: false,
      inbound: null,
      attachments: [],
      originalInbound: null,
      syncing: false,
      inboundItemModal: {
        opened: false,
        item: null,
      },
      addInboundItemsModal: {
        opened: false,
      },
      uploadInboundItemsModal: {
        opened: false,
        index: -1,
        records: [],
      },
      createPoBillsModal: {
        opened: false,
      },
      pos: [],
      saving: false,
      submitting: false,
      sending: false,
      search: '',
      sortOption: { field: 'purchaseOrderName', asc: true },
      file: null,
      newAttributes: {
        mbl: '',
        vessel: '',
        shipmentStatus: '',
        expectedShippingDate: '',
        actualShippingDate: '',
        expectedDeliveryDate: '',
        actualDeliveryDate: '',
        invoiceNums: [],
      },
      newLandedCost: {
        expenseAccountNsId: '259',
        subsidiaryName: '',
        freightVendorName: 'V1074 American Global Logistics',
        freightAccountNsId: '6102',
        freightInvoiceNum: '',
        freightCost: null,
        freightBillNsId: '',
        freightInvoiceDate: '',
        dutyVendorName: '',
        dutyAccountNsId: '',
        dutyEntryNum: '',
        dutyCost: null,
        dutyBillNsId: '',
        dutyInvoiceDate: '',
        saving: false,
        error: '',
      },
      accounts: [],
      locations: [],
      vesselData: null,
      infoWinOpen: false,
    }
  },
  computed: {
    server () {
      return this.$store.state.config.server
    },
    vendorList () {
      return this.$store.state.config.vendorList
    },
    vendorOptions () {
      return this.vendorList.map(item => {
        return {value: item, text: item}
      })
    },
    subsidiaryList () {
      return this.$store.state.config.subsidiaryList
    },
    token () {
      return this.$store.state.user.token
    },
    isVendor () {
      return this.$store.state.user.groups.includes('Vendors')
    },
    isVendorAdmin () {
      return this.$store.state.user.groups.includes('VendorAdmin')
    },
    vendorId () {
      return this.$route.params.vendorId
    },
    myVendors () {
      return this.$store.state.config.myVendors
    },
    vendorName () {
      if (!this.vendorId)
        return ''
      var vendor = this.myVendors.find(v => v.nsId == this.vendorId)
      return vendor ? (vendor.entityId + ': ' + vendor.companyName) : ''
    },
    inboundId () {
      return this.$route.params.inboundId
    },
    editable () {
      return this.inbound && this.inbound.draftStatus == 'Draft' && ((this.isVendor && this.vendorId == this.inbound.vendorId) || this.isVendorAdmin)
    },
    canUpdate () {
      return !this.editable && this.inbound && this.inbound.draftStatus == 'Submitted' && this.inbound.shipmentStatus != 'closed' && this.isVendorAdmin
    },
    newAttributesChanged () {
      var changed = {}
      for (var field in this.newAttributes) {
        if (field == 'invoiceNums') {
          var fieldChanged = false
          if (this.newAttributes[field].length != this.inbound[field].length) {
            fieldChanged = true
          } else {
            for (var i=0;i<this.newAttributes[field].length;i++) {
              if ((this.newAttributes[field][i].container != this.inbound[field][i].container) || (this.newAttributes[field][i].invoiceNum != this.inbound[field][i].invoiceNum)) {
                fieldChanged = true
                break
              }
            }
          }
          changed[field] = fieldChanged
        } else {
          changed[field] = this.newAttributes[field] != this.inbound[field]
        }
      }
      return changed
    },
    inboundChanged () {
      var fields = ['shipmentStatus', 'memo', 'expectedShippingDate', 'expectedDeliveryDate',
        'carrier', 'vessel', 'voyage', 'freightForwarder', 'placeOfReceipt', 'placeOfDelivery',
        'loadPort', 'dischargePort', 'hbl', 'mbl', 'attachments']
      for (var field of fields) {
        if (this.inbound[field] != this.originalInbound[field])
          return true
      }
      if (JSON.stringify(this.inbound.invoiceNums) != JSON.stringify(this.originalInbound.invoiceNums)) {
        return true
      }
      if (this.inbound.items.length != this.originalInbound.items.length) {
        return true
      }
      fields = ['containerInfo', 'itemId', 'poId', 'shipmentItemName', 'shipmentItemDescription', 'quantityExpected']
      for (var i=0;i<this.inbound.items.length;i++) {
        var item = this.inbound.items[i]
        var originalItem = this.originalInbound.items[i]
        for (var field of fields) {
          if (item[field] != originalItem[field]) {
            return true
          }
        }
      }
      return false
    },
    inboundValid () {
      var fields = ['shipmentStatus', 'bookingId', 'expectedShippingDate', 'expectedDeliveryDate',
        'carrier', 'vessel', 'voyage', 'freightForwarder', 'placeOfReceipt', 'placeOfDelivery',
        'loadPort', 'dischargePort', 'hbl', 'mbl']
      for (var field of fields) {
        if (!this.inbound[field])
          return false
      }
      if (!this.inbound.invoiceNums.length) {
        return false
      }
      for (var invoice of this.inbound.invoiceNums) {
        if (!invoice.container || !invoice.container.trim() || !invoice.invoiceNum || !invoice.invoiceNum.trim()) {
          return false
        }
      }
      return this.inbound.items && this.inbound.items.length
    },
    newFreightCostValid () {
      var fs = ['expenseAccountNsId', 'subsidiaryName', 'freightVendorName',  'freightAccountNsId',  'freightInvoiceNum', 'freightCost', 'freightInvoiceDate']
      for (var f of fs) {
        if (!this.newLandedCost[f])
          return false
      }
      return true
    },
    newDutyCostValid () {
      var fs = ['expenseAccountNsId', 'subsidiaryName', 'dutyVendorName', 'dutyAccountNsId', 'dutyEntryNum', 'dutyCost', 'dutyInvoiceDate']
      for (var f of fs) {
        if (!this.newLandedCost[f])
          return false
      }
      return true
    },
    canSave () {
      return true
    },
    canSubmit () {
      return !this.inboundChanged && this.inboundValid && this.inbound.draftStatus == 'Draft' && this.inbound.items.length
    },
    itemsDetails () {
      var items = this.inbound.items.slice()
      var filteredItems = items.filter(i => {
        var s = this.search.toLowerCase()
        return i.purchaseOrderName.toLowerCase().includes(s)
          || i.itemId.toLowerCase().includes(s)
          || i.shipmentItemName.toLowerCase().includes(s)
          || i.shipmentItemDescription.toLowerCase().includes(s)
          || i.containerInfo.toLowerCase().includes(s)
      })
      var sortedItems = filteredItems.sort((a, b) => {
        var field = this.sortOption.field
        var va = a[field]
        var vb = b[field]
        var asc = this.sortOption.asc
        return asc ? va.localeCompare(vb) : vb.localeCompare(va)
      })
      var totals = this.computeTotals(sortedItems)
      return {
        items: sortedItems,
        totals: totals,
      }
    },
    accountOptions () {
      return this.accounts.map(a => {
        var prefix = a.accountNumber.trim() + ' ' + a.type.trim()
        return {value: a.nsId, text: (prefix.trim() ?  (prefix.trim() + ' : ' + a.name) : a.name)}
      })
    },
    canCreatePoBills () {
      return this.inbound.items.length
    },
    dutyWarning () {
      if (!this.inbound || this.inbound.draftStatus =='Draft') {
        return ''
      }
      var actualDuty = this.newLandedCost.dutyCost
      var estimatedDuty = this.inbound && this.inbound.totals && this.inbound.totals.tarriff
      var productCost = this.inbound && this.inbound.totals && this.inbound.totals.amount
      var isCanada = this.inbound && this.inbound.locationName && this.inbound.locationName.includes('Ontario')
      if (!actualDuty || !productCost || isCanada) {
        return ''
      }
      if (!estimatedDuty) {
        return 'Duty not estimated'
      }
      var diff = Math.abs(actualDuty - estimatedDuty)
      var percent = diff / estimatedDuty * 100
      if (percent > 5) {
        return 'Duty cost is more than 5% different from estimated. Please check them below.'
      }
      return ''
    },
    mapCenter () {
      if (this.vesselData && this.vesselData.data && this.vesselData.data.position) {
        var position = this.vesselData.data.position
        return { lat: position.lat, lng: position.lon }
      }
      return { lat: 0, lng: 0 }
    },
    mapMarker () {
      if (!this.vesselData || !this.vesselData.data || !this.vesselData.data.position) {
        return {}
      }
      var position = this.vesselData.data.position
      var svgMarker = {
        path: "M11.437 17.608 3.354 22.828l8.336 -21.536 8.337 21.536L11.944 17.608l-0.253 -0.163 -0.254 0.163Z",
        fillColor: "hsl(348, 100%, 61%)",
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: 'white',
        rotation: position.heading,
        scale: 1.5,
        anchor: {x: 12, y: 12},
      };
      var label = {
        text: this.inbound.vessel,
        className: 'my-marker-label',
      }
      
      return {
        position: this.mapCenter,
        icon: svgMarker,
        label: label,
      }
    },
    infoOptions () {
      if (!this.vesselData || !this.vesselData.data || !this.vesselData.data.position) {
        return {}
      }
      var content = this.makeVesselContent(this.vesselData.data)
      return {
        content: content,
        pixelOffset: {
          width: 0,
          height: -25
        }
      }
    },
    succeededPoBills () {
      if (!this.inbound || !this.inbound.poBills) {
        return []
      }
      return this.inbound.poBills.filter(b => Boolean(b.poBillId))
    },
    failedPoBills () {
      if (!this.inbound || !this.inbound.poBills) {
        return []
      }
      return this.inbound.poBills.filter(b => !Boolean(b.poBillId))
    },
  },
  created () {
    if (this.inboundId == 'new') {
      this.makeNewInbound()
    } else {
      this.getInboundDetail()
    }
  },
  watch: {
    inboundId (newVal, oldVal) {
      if (newVal != oldVal) {
        if (newVal == 'new') {
          this.makeNewInbound()
        } else {
          this.getInboundDetail()
        }
      }
    },
    attachments (val) {
      this.inbound.attachments = val.join(',')
    },
  },
  methods: {
    getInboundDetail () {
      this.waiting = true
      this.$http.get(this.server + '/myapp/get-inbound-detail/' + this.inboundId + '/').then(resp => {
        var inbound = resp.body
        var isCanada = inbound.locationName.includes('Ontario')
        for (var i=0;i<inbound.items.length;i++) {
          var item = inbound.items[i]
          item.idx = i
          if (item.po && item.poItem) {
            if (isCanada) {
              item.tarriff = item.poItem.dutyPercentageCanada
            } else {
              item.tarriff = item.poItem.dutyPercentage
            }
          }
        }
        inbound.isCanada = isCanada
        inbound.totals = this.computeTotals(inbound.items)
        this.inbound = inbound
        this.newAttributes = {
          shipmentStatus: this.inbound.shipmentStatus,
          mbl: this.inbound.mbl,
          vessel: this.inbound.vessel,
          expectedShippingDate: this.inbound.expectedShippingDate,
          expectedDeliveryDate: this.inbound.expectedDeliveryDate,
          actualShippingDate: this.inbound.actualShippingDate,
          actualDeliveryDate: this.inbound.actualDeliveryDate,
          invoiceNums: JSON.parse(JSON.stringify(this.inbound.invoiceNums)),
        }
        this.newLandedCost.subsidiaryName = isCanada ? 'VANITY ART INC' : 'VANITY ART LLC'
        this.newLandedCost.dutyVendorName = isCanada ? 'V0432 Canada Border Services Agency' : 'V0901 U.S. Customs & Border Protection'
        this.newLandedCost.dutyAccountNsId = isCanada ? '214' : '1262'
        this.newLandedCost = {...this.newLandedCost, ...JSON.parse(JSON.stringify(inbound.landedCost))}
        this.attachments = this.inbound.attachments.split(',').filter(i => i)
        this.originalInbound = JSON.parse(JSON.stringify(this.inbound))
        if (this.inbound.shipmentStatus == 'inTransit' && this.isVendorAdmin) {
          this.getVesselData()
        }
        this.waiting = false
      }, err => {
        this.error = err.body
        this.waiting = false
      })
    },
    computeTotals (items) {
      var totals = {
        quantity: 0,
        amount: 0,
        tarriff: 0,
      }
      for (var item of items) {
        totals.quantity += item.quantityExpected
        totals.amount += (item.poItem && item.poItem.rate) ? (item.poItem.rate * item.quantityExpected) : 0
        totals.tarriff += (item.poItem && item.tarriff) ? (item.poItem.rate * item.quantityExpected * item.tarriff / 100) : 0
      }
      return totals
    },
    syncFromNS () {
      this.syncing = true
      var data = {
        nsId: this.inbound.nsId,
      }
      this.$http.post(this.server + '/myapp/sync-inbound/', data).then(resp => {
        this.getInboundDetail()
        this.syncing = false
      }, err => {
        this.error = err.body
        this.syncing = false
      })
    },
    makeNewInbound () {
      this.inbound = {
        shipmentNumber: '',
        bookingId: '',
        externalDocumentNumber: '',
        nsId: '',
        shipmentStatus: '',
        vendorId: this.vendorId,
        vendorName: this.vendorName,
        draftStatus: 'Draft',
        memo: '',
        shipmentCreatedDate: '',
        expectedShippingDate: '',
        actualShippingDate: '',
        expectedDeliveryDate: '',
        lastModifiedDate: '',
        actualDeliveryDate: '',
        carrier: '',
        vessel: '',
        voyage: '',
        freightForwarder: '',
        placeOfReceipt: '',
        placeOfDelivery: '',
        loadPort: '',
        dischargePort: '',
        hbl: '',
        mbl: '',
        items: [],
        invoiceNums: [],
        attachments: '',
      }
      this.attachments = []
      this.originalInbound = JSON.parse(JSON.stringify(this.inbound))
    },
    openInboundItemModal (item) {
      this.inboundItemModal.item = item
      this.inboundItemModal.opened = true
    },
    closeInboundItemModal (obj) {
      if (obj) {
        var idx = obj.item.idx
        var index = this.inbound.items.findIndex(i => i.idx == idx)
        var ss = obj.poItem.itemName.split(':')
        var itemName = ss[ss.length-1].trim()
        var item = {
          id: obj.item ? obj.item.id : '',
          idx: idx,
          itemId: obj.poItem.itemId,
          poId: obj.po.id,
          poLine: obj.poItem.line,
          poVendorId: obj.po.vendorId,
          purchaseOrderId: obj.po.nsId,
          purchaseOrderName: 'PO#' + obj.po.tranId,
          shipmentItemId: obj.item ? obj.item.shipmentItemId : '',
          shipmentItemName: itemName,
          shipmentItemDescription: obj.poItem.description,
          shipmentItemClass: obj.poItem.className,
          containerInfo: obj.shipmentContainer + '(' + obj.shipmentQuantity + ')',
          quantityExpected: obj.shipmentQuantity,
          po: JSON.parse(JSON.stringify(obj.po)),
          poItem: JSON.parse(JSON.stringify(obj.poItem)),
        }
        this.inbound.items.splice(index, 1, item)
        this.inbound.totals = this.computeTotals(this.inbound.items)
      }
      this.inboundItemModal.opened = false
    },
    openAddInboundItemsModal () {
      this.addInboundItemsModal.opened = true
    },
    closeAddInboundItemsModal (objs) {
      if (objs) {
        var len = this.inbound.items.length
        var items = objs.map((obj, idx) => {
          var ss = obj.poItem.itemName.split(':')
          var itemName = ss[ss.length-1].trim()
          return {
            id: '',
            idx: len + idx,
            itemId: obj.poItem.itemId,
            poId: obj.po.id,
            poLine: obj.poItem.line,
            poVendorId: obj.po.vendorId,
            purchaseOrderId: obj.po.nsId,
            purchaseOrderName: 'PO#' + obj.po.tranId,
            shipmentItemId: obj.item ? obj.item.shipmentItemId : '',
            shipmentItemName: itemName,
            shipmentItemDescription: obj.poItem.description,
            shipmentItemClass: obj.poItem.className,
            containerInfo: obj.shipmentContainer + '(' + obj.shipmentQuantity + ')',
            quantityExpected: obj.shipmentQuantity,
            po: JSON.parse(JSON.stringify(obj.po)),
            poItem: JSON.parse(JSON.stringify(obj.poItem)),
          }
        })
        this.inbound.items.push(...items)
        this.inbound.totals = this.computeTotals(this.inbound.items)
      }
      this.addInboundItemsModal.opened = false
    },
    openUploadInboundItemsModal (records) {
      this.uploadInboundItemsModal.records = records
      this.uploadInboundItemsModal.opened = true
    },
    closeUploadInboundItemsModal (objs) {
      if (objs) {
        var len = this.inbound.items.length
        var items = objs.map((obj, idx) => {
          var ss = obj.poItem.itemName.split(':')
          var itemName = ss[ss.length-1].trim()
          return {
            id: '',
            idx: len + idx,
            itemId: obj.poItem.itemId,
            poId: obj.po.id,
            poLine: obj.poItem.line,
            poVendorId: obj.po.vendorId,
            purchaseOrderId: obj.po.nsId,
            purchaseOrderName: 'PO#' + obj.po.tranId,
            shipmentItemId: obj.item ? obj.item.shipmentItemId : '',
            shipmentItemName: itemName,
            shipmentItemDescription: obj.poItem.description,
            shipmentItemClass: obj.poItem.className,
            containerInfo: obj.shipmentContainer + '(' + obj.shipmentQuantity + ')',
            quantityExpected: obj.shipmentQuantity,
            po: JSON.parse(JSON.stringify(obj.po)),
            poItem: JSON.parse(JSON.stringify(obj.poItem)),
          }
        })
        this.inbound.items.push(...items)
        this.inbound.totals = this.computeTotals(this.inbound.items)
      }
      this.uploadInboundItemsModal.opened = false
    },
    deleteInboundItem (item) {
      var confirm = {
        title: 'Remove item',
        message: 'Are you sure to remove this item?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.deleteInboundItemConfirmed,
          args: [item]
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    deleteInboundItemConfirmed (item) {
      var items = []
      var count = 0
      for (var i of this.inbound.items.slice()) {
        if (i.idx != item.idx) {
          i.idx = count
          count++
          items.push(i)
        }
      }
      this.inbound.items = items
    },
    getOpenPos () {
      this.$http.get(this.server + '/myapp/get-all-purchase-orders/').then(resp => {
        this.pos = resp.body.filter(po => po.statusId != 'Closed' && po.approvalStatus == '2' && po.confirmedBy)
      })
    },
    getVendorPos () {
      this.$http.get(this.server + '/myapp/get-vendor-purchase-orders/' + this.vendorId).then(resp => {
        this.pos = resp.body.filter(po => po.statusId != 'Closed' && po.statusId != 'Fully Billed' && po.approvalStatus == '2' && po.confirmedBy)
      })
    },
    saveDraft () {
      if (!this.inboundChanged || !this.canSave || this.saving) {
        return
      }
      this.saving = true
      this.$http.post(this.server + '/myapp/save-inbound-draft/', this.inbound).then(resp => {
        if (this.inboundId == 'new') {
          this.$router.replace('/vendor-inbound/' + this.vendorId + '/' + resp.body.id)
        } else {
          this.getInboundDetail()
        }
        this.saving = false
      }, err => {
        this.error = err.body
        this.saving = false
      })
    },
    submitFinal () {
      if (!this.canSubmit || this.submitting) {
        return
      }
      var confirm = {
        title: 'Submit final',
        message: 'Are you sure to submit this inbound shipment as final? After successful submission, the inbound shipment cannot be updated anymore',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.submitFinalConfirmed,
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    submitFinalConfirmed () {
      this.submitting = true
      this.$http.post(this.server + '/myapp/submit-inbound-final/', this.inbound).then(resp => {
        this.getInboundDetail()
        this.submitting = false
      }, err => {
        this.error = err.body
        this.submitting = false
      })
    },
    submitItems () {
      this.submitting = true
      this.$http.post(this.server + '/myapp/submit-inbound-items/', this.inbound).then(resp => {
        this.getInboundDetail()
        this.submitting = false
      }, err => {
        this.error = err.body
        this.submitting = false
      })
    },
    deleteDraft () {
      var confirm = {
        title: 'Delete draft',
        message: 'Are you sure to delete this inbound shipment draft? This action cannot be undone',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.deleteDraftConfirmed,
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    deleteDraftConfirmed () {
      this.$http.post(this.server + '/myapp/delete-inbound-draft/', this.inbound).then(resp => {
        this.$router.replace('/vendor-inbounds/' + this.vendorId)
      }, err => {
        this.error = err.body
      })
    },
    addAttachment (attachment) {
      this.attachments.push(attachment.id)
    },
    removeAttachment (attachmentId) {
      var index = this.attachments.indexOf(attachmentId)
      if (index > -1) {
        this.attachments.splice(index, 1)
      }
    },
    putBackDraft () {
      var confirm = {
        title: 'Put back draft',
        message: 'Are you sure to put this inbound shipment back to draft?',
        button: 'Yes, I am sure.',
        callback: {
          context: this,
          method: this.putBackDraftConfirmed,
        }
      }
      this.$store.commit('modals/openConfirmModal', confirm)
    },
    putBackDraftConfirmed () {
      this.sending = true
      this.$http.post(this.server + '/myapp/put-back-inbound-draft/', this.inbound).then(resp => {
        this.getInboundDetail()
        this.sending = false
      }, err => {
        this.error = err.body
        this.sending = false
      })
    },
    formatPrice (value) {
      if (!value) {
        return ''
      }
      return '$' + value.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        useGrouping: false
      })
    },
    changeSortOption (field) {
      this.sortOption = {
        field: field,
        asc: field == this.sortOption.field ? !this.sortOption.asc : true
      }
    },
    onFileChange (e) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length)
        return;
      this.file = files[0];
    },
    upload () {
      if (!this.file) {
        return
      }
      var vm = this
      var reader = new FileReader()
      reader.onload = function () {
        var text = reader.result
        var records = parse(text, {
          columns: true,
        })
        console.log(records)
        vm.openUploadInboundItemsModal(records)
      }
      reader.readAsText(this.file)
    },
    updateAttribute (field) {
      if (!this.newAttributesChanged[field]) {
        return
      }
      var message = {
        id: this.inbound.id,
        [field]: this.newAttributes[field],
      }
      this.sending = false
      this.$http.post(this.server + '/myapp/update-submitted-inbound/', message).then(resp => {
        this.sending = false
        this.getInboundDetail()
      }, err => {
        this.sending = false
        this.error = err.body
      })
    },
    getAccounts () {
      this.$http.get(this.server + '/myapp/get-accounts/').then(resp => {
        this.accounts = resp.body
      }, err => {
        this.error = err.body
      })
    },
    submitFreightCost () {
      if(!this.newFreightCostValid) {
        return
      }
      this.newLandedCost.saving = true
      var message = {
        inboundId: this.inboundId,
        landedCost: this.newLandedCost,
        submitFreightCost: true,
      }
      this.$http.post(this.server + '/myapp/save-inbound-landed-cost/', message).then(resp => {
        this.newLandedCost = {...this.newLandedCost, ...resp.body}
        this.newLandedCost.saving = false
      }, err => {
        this.newLandedCost.error = err.body
        this.newLandedCost.saving = false
      })
    },
    submitDutyCost () {
      if(!this.newDutyCostValid) {
        return
      }
      this.newLandedCost.saving = true
      var message = {
        inboundId: this.inboundId,
        landedCost: this.newLandedCost,
        submitDutyCost: true,
      }
      this.$http.post(this.server + '/myapp/save-inbound-landed-cost/', message).then(resp => {
        this.newLandedCost = {...this.newLandedCost, ...resp.body}
        this.newLandedCost.saving = false
      }, err => {
        this.newLandedCost.error = err.body
        this.newLandedCost.saving = false
      })
    },
    saveLandedCost () {
      this.newLandedCost.saving = true
      var message = {
        inboundId: this.inboundId,
        landedCost: this.newLandedCost,
      }
      this.$http.post(this.server + '/myapp/save-inbound-landed-cost/', message).then(resp => {
        this.newLandedCost.saving = false
      }, err => {
        this.newLandedCost.error = err.body
        this.newLandedCost.saving = false
      })
    },
    closeCreatePoBillsModal () {
      this.createPoBillsModal.opened = false
    },
    PoBillsCreated () {
      this.getInboundDetail()
    },
    addInvoiceNum () {
      this.inbound.invoiceNums.push({container: '', invoiceNum: ''})
    },
    removeInvoiceNum (i) {
      this.inbound.invoiceNums.splice(i, 1)
    },
    uploadToNs () {
      var message = { attachmentId: 39 }
      this.$http.post(this.server + '/myapp/upload-attachment-to-ns/', message)
    },
    getVesselData () {
      var message = {
        vesselName: this.inbound.vessel.trim(),
      }
      this.$http.post(this.server + '/myapp/get-vessel-by-name/', message).then(resp => {
        this.vesselData = resp.body
        this.infoWinOpen = true
      }, err => {
        this.vesselData = {error: err.body}
      })
    },
    makeVesselContent (data) {
      var {position, voyage, summary} = data
      var content = converter.makeHtml(summary.position)
      content = `<div class="mb-5 is-size-6">${content}</div>`
      var rows = [
        {
          data: position,
          fields: [
            {name: 'areaName', label: 'Area'},
            {name: 'lat', label: 'Latitude'},
            {name: 'lon', label: 'Longitude'},
            {name: 'heading', label: 'Heading'},
          ],
        },
        {
          data: position,
          fields: [
            {name: 'navigationalStatus', label: 'Status'},
            {name: 'speed', label: 'Speed'},
            {name: 'course', label: 'Course'},
            {name: 'timestamp', label: 'Timestamp', formatter: dateFormatter},
          ],
        },
        {
          data: voyage,
          fields: [
            {name: 'reportedDestination', label: 'Destination'},
            {name: 'eta', label: 'ETA', formatter: dateFormatter},
            {name: 'progress', label: 'Progress'},
            {name: 'arrivalLabel', label: 'Arrival Label'},
          ],
        },
      ]
      for (var row of rows) {
        content += this.makeLevelRow(row.data, row.fields)
      }
      content = `<div class="ml-3 mr-3 mb-3">${content}</div>`
      return content
    },
    makeLevelRow (data, fields) {
      var blocks = fields.map(field => this.makeLevelBlock(data, field))
      return `
        <div class="columns">
          ${blocks.join('')}
        </div>
      `
    },
    makeLevelBlock (data, field) {
      var label = field.label
      var value = data[field.name]
      if (value) {
        if (field.formatter) {
          value = field.formatter(value)
        } else {
          value = value.toString()
        }
      } else {
        value = ''
      }
      return `
        <div class="column" style="padding-top:6px;padding-bottom:6px">
          <p class="heading">${label}</p>
          <p class="title is-6">${value}</p>
        </div>
      `
    },
  },
  mounted () {
    this.$nextTick(function(){
      if (this.inboundId == 'new') {
        this.makeNewInbound()
      } else {
        this.getInboundDetail()
      }
      if (this.isVendorAdmin) {
        this.getOpenPos()
      } else if (this.vendorId) {
        this.getVendorPos()
      }
      this.getAccounts()
      this.uploadToNs()
    })
  },
}
</script>

<style scoped>
.sub-items-table {
  font-size: 0.9em;
}
.num-cell {
  text-align: right !important;
}
.action-column {
  min-width: 100px;
}
.upload-info {
  margin-top: -10px
}
.model-select-full-width {
  .ui {
    height: 100%;
  }
}
.model-select-350px {
  .ui {
    height: 100%;
    width: 350px;
  }
}

.amount-input {
  width: 120px;
}
.date-input {
  width: 120px;
}
</style>
